/**
 * FileFieldType — załącznik. Storage: PG TEXT (klucz MinIO).
 *
 * renderInput: wybór pliku → upload do /api/modules/:moduleId/upload → onChange(key).
 * renderCell:  pokazuje nazwę pliku (ostatni segment klucza MinIO).
 */

import { useRef, useState } from 'react';
import { Paperclip, X } from 'lucide-react';

import { api } from '@/lib/api';

import type { FieldInputProps, FieldTypeDefinition } from '../FieldRegistry';

function FileInput({ field, value, onChange, disabled }: FieldInputProps) {
  const currentKey = typeof value === 'string' ? value : '';
  const filename = currentKey ? (currentKey.split('/').pop() ?? currentKey) : '';

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fieldSlug', field.slug);

      const res = await api.post<{ key: string; filename: string }>(
        `/modules/${field.moduleId}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      onChange(res.data.key);
    } catch {
      setError('Nie udało się przesłać pliku. Spróbuj ponownie.');
    } finally {
      setUploading(false);
      // Reset input żeby można ponownie wybrać ten sam plik
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleClear = () => {
    onChange(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div data-testid={`field-${field.slug}-input`} className="space-y-1">
      {currentKey ? (
        <div className="flex items-center gap-2 rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-sm">
          <Paperclip className="h-4 w-4 shrink-0 text-text-muted" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate text-text-secondary" title={filename}>
            {filename}
          </span>
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="shrink-0 rounded p-0.5 text-text-muted hover:text-error-500"
              aria-label="Usuń plik"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ) : (
        <label
          className={
            'flex cursor-pointer items-center gap-2 rounded-md border border-dashed px-3 py-3 text-sm transition ' +
            (disabled
              ? 'cursor-not-allowed border-border-primary text-text-muted'
              : 'border-border-primary text-text-secondary hover:border-accent-500 hover:text-accent-500')
          }
        >
          <Paperclip className="h-4 w-4 shrink-0" aria-hidden="true" />
          {uploading ? (
            <span className="text-text-muted">Przesyłanie…</span>
          ) : (
            <span>{disabled ? '— brak załącznika —' : 'Wybierz plik…'}</span>
          )}
          <input
            ref={inputRef}
            type="file"
            className="sr-only"
            disabled={disabled || uploading}
            onChange={handleFileChange}
          />
        </label>
      )}
      {error && <p className="text-xs text-error-500">{error}</p>}
    </div>
  );
}

export const FileFieldType: FieldTypeDefinition = {
  type: 'file',
  renderInput: (props) => <FileInput {...props} />,
  renderCell: (_field, value) => {
    if (typeof value !== 'string' || !value) return '';
    return value.split('/').pop() ?? value;
  },
  defaultValue: null,
};
